import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Send, Package } from 'lucide-react';
import { MagneticButton } from './MagneticButton';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ContactSection = () => {
  const [activeTab, setActiveTab] = useState('contact');
  const [isLoading, setIsLoading] = useState(false);

  // Contact form state
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });

  // Quotation form state
  const [quotationForm, setQuotationForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    products: '',
    quantity: '',
    message: '',
  });

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await axios.post(`${API}/contact`, contactForm);
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      setContactForm({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
      console.error('Contact form error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuotationSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await axios.post(`${API}/quotation`, quotationForm);
      toast.success('Quotation request submitted! Our team will contact you shortly.');
      setQuotationForm({ name: '', email: '', phone: '', company: '', products: '', quantity: '', message: '' });
    } catch (error) {
      toast.error('Failed to submit request. Please try again.');
      console.error('Quotation form error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 lg:py-32 relative">
      {/* Background */}
      <div className="absolute inset-0 bg-[#FDFBF7]" />
      <div className="blob-container">
        <div className="golden-blob blob w-[400px] h-[400px] bottom-[-100px] left-[-100px]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-[#DAB668] text-sm font-medium tracking-widest uppercase mb-4 block">
            Get in Touch
          </span>
          <h2 data-testid="contact-heading" className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#2C2C2C] mb-6">
            Let's Start a Conversation
          </h2>
          <p className="text-[#6E6A62] max-w-2xl mx-auto">
            Have questions about our products or want to request a quotation? We'd love to hear from you.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-16">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h3 className="font-serif text-2xl text-[#2C2C2C] mb-8">Contact Information</h3>
            
            <div className="space-y-6">
              <div className="flex items-start gap-4" data-testid="contact-email">
                <div className="w-12 h-12 rounded-full bg-[#DAB668]/10 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-[#DAB668]" />
                </div>
                <div>
                  <p className="text-sm text-[#6E6A62] mb-1">Email</p>
                  <a href="mailto:shop@buttershine.com" className="text-[#2C2C2C] hover:text-[#DAB668] transition-colors">
                    shop@buttershine.com
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4" data-testid="contact-phone">
                <div className="w-12 h-12 rounded-full bg-[#DAB668]/10 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-[#DAB668]" />
                </div>
                <div>
                  <p className="text-sm text-[#6E6A62] mb-1">Phone</p>
                  <a href="tel:+919945699525" className="text-[#2C2C2C] hover:text-[#DAB668] transition-colors">
                    +91 99456 99525
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4" data-testid="contact-address">
                <div className="w-12 h-12 rounded-full bg-[#DAB668]/10 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-[#DAB668]" />
                </div>
                <div>
                  <p className="text-sm text-[#6E6A62] mb-1">Address</p>
                  <p className="text-[#2C2C2C]">
                    Building Veena, D.No.V9-7-120/23(3)<br />
                    Street Bajal Road, Mangaluru<br />
                    Dakshina Kannada, Karnataka - 575009
                  </p>
                </div>
              </div>
            </div>

            {/* Founder Note */}
            <div className="mt-12 p-6 bg-[#DAB668]/5 rounded-2xl border border-[#DAB668]/10">
              <p className="text-[#6E6A62] italic leading-relaxed">
                "We're here to help you discover the perfect wellness tools for your journey. 
                Don't hesitate to reach out!"
              </p>
              <p className="mt-4 font-medium text-[#2C2C2C]">— Varun Mohan, Founder</p>
            </div>
          </motion.div>

          {/* Forms */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {/* Tab Navigation */}
            <div className="flex gap-4 mb-8">
              <button
                data-testid="contact-tab-btn"
                onClick={() => setActiveTab('contact')}
                className={`flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium transition-all ${
                  activeTab === 'contact'
                    ? 'bg-[#DAB668] text-white'
                    : 'bg-[#DAB668]/10 text-[#2C2C2C] hover:bg-[#DAB668]/20'
                }`}
              >
                <Send size={16} />
                Contact Us
              </button>
              <button
                data-testid="quotation-tab-btn"
                onClick={() => setActiveTab('quotation')}
                className={`flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium transition-all ${
                  activeTab === 'quotation'
                    ? 'bg-[#DAB668] text-white'
                    : 'bg-[#DAB668]/10 text-[#2C2C2C] hover:bg-[#DAB668]/20'
                }`}
              >
                <Package size={16} />
                Request Quotation
              </button>
            </div>

            {/* Contact Form */}
            {activeTab === 'contact' && (
              <motion.form
                data-testid="contact-form"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                onSubmit={handleContactSubmit}
                className="space-y-6"
              >
                <div>
                  <input
                    type="text"
                    data-testid="contact-name-input"
                    placeholder="Your Name"
                    required
                    value={contactForm.name}
                    onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                    className="form-input gold-focus"
                  />
                </div>
                <div>
                  <input
                    type="email"
                    data-testid="contact-email-input"
                    placeholder="Email Address"
                    required
                    value={contactForm.email}
                    onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                    className="form-input gold-focus"
                  />
                </div>
                <div>
                  <input
                    type="tel"
                    data-testid="contact-phone-input"
                    placeholder="Phone Number (Optional)"
                    value={contactForm.phone}
                    onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                    className="form-input gold-focus"
                  />
                </div>
                <div>
                  <textarea
                    data-testid="contact-message-input"
                    placeholder="Your Message"
                    required
                    rows={4}
                    value={contactForm.message}
                    onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                    className="form-input gold-focus resize-none"
                  />
                </div>
                <MagneticButton
                  type="submit"
                  data-testid="contact-submit-btn"
                  disabled={isLoading}
                  className="w-full py-4 bg-[#DAB668] text-white rounded-full font-medium hover:bg-[#C5A152] transition-colors disabled:opacity-50"
                >
                  {isLoading ? 'Sending...' : 'Send Message'}
                </MagneticButton>
              </motion.form>
            )}

            {/* Quotation Form */}
            {activeTab === 'quotation' && (
              <motion.form
                data-testid="quotation-form"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                onSubmit={handleQuotationSubmit}
                className="space-y-6"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <input
                    type="text"
                    data-testid="quotation-name-input"
                    placeholder="Your Name"
                    required
                    value={quotationForm.name}
                    onChange={(e) => setQuotationForm({ ...quotationForm, name: e.target.value })}
                    className="form-input gold-focus"
                  />
                  <input
                    type="email"
                    data-testid="quotation-email-input"
                    placeholder="Email Address"
                    required
                    value={quotationForm.email}
                    onChange={(e) => setQuotationForm({ ...quotationForm, email: e.target.value })}
                    className="form-input gold-focus"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <input
                    type="tel"
                    data-testid="quotation-phone-input"
                    placeholder="Phone Number"
                    value={quotationForm.phone}
                    onChange={(e) => setQuotationForm({ ...quotationForm, phone: e.target.value })}
                    className="form-input gold-focus"
                  />
                  <input
                    type="text"
                    data-testid="quotation-company-input"
                    placeholder="Company (Optional)"
                    value={quotationForm.company}
                    onChange={(e) => setQuotationForm({ ...quotationForm, company: e.target.value })}
                    className="form-input gold-focus"
                  />
                </div>
                <div>
                  <input
                    type="text"
                    data-testid="quotation-products-input"
                    placeholder="Products Interested In"
                    required
                    value={quotationForm.products}
                    onChange={(e) => setQuotationForm({ ...quotationForm, products: e.target.value })}
                    className="form-input gold-focus"
                  />
                </div>
                <div>
                  <input
                    type="text"
                    data-testid="quotation-quantity-input"
                    placeholder="Quantity Required"
                    value={quotationForm.quantity}
                    onChange={(e) => setQuotationForm({ ...quotationForm, quantity: e.target.value })}
                    className="form-input gold-focus"
                  />
                </div>
                <div>
                  <textarea
                    data-testid="quotation-message-input"
                    placeholder="Additional Details (Optional)"
                    rows={3}
                    value={quotationForm.message}
                    onChange={(e) => setQuotationForm({ ...quotationForm, message: e.target.value })}
                    className="form-input gold-focus resize-none"
                  />
                </div>
                <MagneticButton
                  type="submit"
                  data-testid="quotation-submit-btn"
                  disabled={isLoading}
                  className="w-full py-4 bg-[#DAB668] text-white rounded-full font-medium hover:bg-[#C5A152] transition-colors disabled:opacity-50"
                >
                  {isLoading ? 'Submitting...' : 'Request Quotation'}
                </MagneticButton>
              </motion.form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
